import { ModuleConfig, MapIcon } from '@schamburg-web/interfaces';
import React, { useEffect, useMemo, useState } from 'react';
import { useMapContext } from '../../contexts';
import { useModuleData } from '../../util/useModuleData';
import { validLatLng } from '../../util/MapUtil';
import GoogleAdvancedMarker from './GoogleAdvancedMarker';
import GoogleClusterLayer from './GoogleClusterLayer';

interface GoogleEndpointLayerProps {
  module: ModuleConfig;
}

const GoogleEndpointLayer: React.FC<GoogleEndpointLayerProps> = ({
  module,
}) => {
  const isClusterLayer = useMemo(() => {
    return module.mapConfig?.clusterLayer || false;
  }, [module]);

  if (module.apiConfig?.endpoint) {
    if (isClusterLayer) {
      return <GoogleClusterLayer module={module} />;
    } else return <Layer module={module} />;
  } else {
    return null;
  }
};

const Layer: React.FC<GoogleEndpointLayerProps> = ({ module }) => {
  const { displayObjects, isEnabled, isLoading } = useModuleData(module.name);
  const [icons, setIcons] = useState<MapIcon[]>([]);
  const { addMapMessage, removeMapMessage } = useMapContext();
  const [previousEnabled, setPreviousEnabled] = useState<boolean>(isEnabled);
  const [hasMounted, setHasMounted] = useState<boolean>(false);

  useEffect(() => {
    if (isEnabled && displayObjects.length > 0) {
      const filteredObjects = displayObjects.filter((obj: MapIcon) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return validLatLng(obj as any);
      });
      setIcons(filteredObjects);
    } else {
      setIcons([]);
    }
  }, [module.name, displayObjects, isEnabled]);

  // Show "no data" message when user enables a layer with no data
  useEffect(() => {
    const wasJustEnabled = !previousEnabled && isEnabled;

    let timeoutId: NodeJS.Timeout | undefined;

    // Only show message if component has mounted (not initial load)
    if (wasJustEnabled && hasMounted && !isLoading) {
      timeoutId = setTimeout(() => {
        if (displayObjects.length === 0) {
          const message = `No icons found for ${module.moduleLabel}`;
          addMapMessage(message);
          setTimeout(() => removeMapMessage(message), 1500);
        }
      }, 100);
    }

    // Update state after checking conditions
    if (previousEnabled !== isEnabled) {
      setPreviousEnabled(isEnabled);
    }
    if (!hasMounted) {
      setHasMounted(true);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isLoading, displayObjects.length]);

  if (icons.length > 0) {
    return (
      <>
        {icons.map((obj: MapIcon) => (
          <GoogleAdvancedMarker iconObj={obj} module={module} key={obj.id} />
        ))}
      </>
    );
  } else {
    return null;
  }
};

export default GoogleEndpointLayer;
